import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import {
  CallRecordingMode,
  IClinicE911Address,
  IClinicPhoneDevice,
  ISipCredentialsOnce,
} from '@dataTypes/clinic.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  createDeviceE911AddressAction,
  createPhoneDeviceAction,
  deleteE911AddressAction,
  fetchPhoneManagementData,
  regenerateDeviceSipCredentialsAction,
  updatePhoneDeviceAction,
} from '@store/actions/settings-communication.action'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useTeamCatalog } from '@context/TeamCatalog.context'
import { toStateCode } from '@utils/address.util'
import { areValuesEqual } from '@utils/settings.util'
import {
  callRecordingModeFromBooleans,
  callRecordingModeToBooleans,
} from '@utils/clinic.util'

const LOADING_CONTAINER = 'settings-add-device'

export type E911FormState = {
  customer_name: string
  address: string
  city: string
  region: string
  postal_code: string
}

export type UseAddDeviceArgs = {
  initialDevice: IClinicPhoneDevice | null
  onCancel: () => void
  onSaved: () => void
}

export interface IAddDeviceHooks {
  baseline: IClinicPhoneDevice
  callRecordingMode: CallRecordingMode
  deviceData: IClinicPhoneDevice
  discardUnsavedChanges: () => Promise<void>
  extensionOptions: string[]
  handleCallRecordingChange: (mode: CallRecordingMode) => void
  handleChange: (key: keyof IClinicPhoneDevice, value: string | boolean) => void
  handleSave: () => Promise<void>
  hasUnsavedChanges: () => boolean
  onClickCancel: () => Promise<void>
  onHide: () => Promise<void>
  saving: boolean
  setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
  setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
  showUnsavedChangesModal: boolean
  validationErrors: Record<string, string>
  userOptions: { user_id: string; label: string }[]

  // E911
  defaultE911Form: E911FormState
  e911Form: E911FormState
  e911Linked: boolean
  pendingRemoveE911: boolean
  removeE911FromDevice: () => Promise<void>
  savedE911: IClinicE911Address | undefined
  setE911Form: Dispatch<SetStateAction<E911FormState>>
  setPendingRemoveE911: Dispatch<SetStateAction<boolean>>
  setShowE911Modal: Dispatch<SetStateAction<boolean>>
  showE911Modal: boolean
  submitE911Flow: () => Promise<void>

  // SIP
  dismissSipOnceModal: () => void
  regenerateSip: () => Promise<void>
  sipBusy: boolean
  sipOnceModal: ISipCredentialsOnce | null
}

const isExtensionInRange = (raw: string | null | undefined): boolean => {
  const v = String(raw || '').replace(/\D/g, '')
  if (!v) return false
  const n = Number.parseInt(v, 10)
  return Number.isInteger(n) && n >= 100 && n <= 900
}

const isE164 = (raw: string | null | undefined): boolean => {
  if (!raw) return false
  return /^\+[1-9]\d{6,14}$/.test(String(raw).trim())
}

const useAddDevice = (args: UseAddDeviceArgs): IAddDeviceHooks => {
  const { teams } = useTeamCatalog()
  const { initialDevice, onCancel, onSaved } = args
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''
  const phoneDevices = useAppSelector(
    (s) => s.settingsCommunication.phoneDevices,
  )
  const internalExtensions = useAppSelector(
    (s) => s.settingsCommunication.internalExtensions,
  )
  const providers = useAppSelector((s) => s.provider.data)

  const defaultE911Form: E911FormState = useMemo(
    () => ({
      customer_name: selectedClinic?.clinic_name ?? '',
      address: selectedClinic?.address ?? '',
      city: selectedClinic?.city ?? '',
      region: toStateCode(selectedClinic?.state ?? ''),
      postal_code: selectedClinic?.zip ?? '',
    }),
    [
      selectedClinic?.address,
      selectedClinic?.city,
      selectedClinic?.clinic_name,
      selectedClinic?.state,
      selectedClinic?.zip,
    ],
  )

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})
  const [showE911Modal, setShowE911Modal] = useState(false)
  const [e911Form, setE911Form] = useState<E911FormState>(defaultE911Form)
  const [pendingRemoveE911, setPendingRemoveE911] = useState(false)
  const [sipOnceModal, setSipOnceModal] = useState<ISipCredentialsOnce | null>(
    null,
  )
  const [sipBusy, setSipBusy] = useState(false)
  const [saving, setSaving] = useState(false)

  const baseline = useMemo((): IClinicPhoneDevice => {
    if (initialDevice) {
      return {
        ...initialDevice,
        record_inbound_calls: !!initialDevice.record_inbound_calls,
        record_outbound_calls: !!initialDevice.record_outbound_calls,
      }
    }
    return {
      clinic_id: clinicId,
      clinic_internal_extension_id: null,
      device_name: '',
      device_type: 'soft_phone',
      device_extension_number: '',
      team: '',
      assigned_user_id: null,
      sip_uri: '',
      pstn_number: '',
      record_inbound_calls: false,
      record_outbound_calls: false,
    }
  }, [clinicId, initialDevice])

  const [deviceData, setDeviceData] = useState<IClinicPhoneDevice>(baseline)
  const e911Addresses = useAppSelector(
    (s) => s.settingsCommunication.e911Addresses,
  )
  const savedE911 = e911Addresses.find(
    (e) => e.clinic_e911_address_id === deviceData.clinic_e911_address_id,
  )

  useEffect(() => {
    setDeviceData(baseline)
  }, [baseline])

  const callRecordingMode = useMemo(
    () =>
      callRecordingModeFromBooleans(
        deviceData.record_inbound_calls,
        deviceData.record_outbound_calls,
      ),
    [deviceData.record_inbound_calls, deviceData.record_outbound_calls],
  )

  const userOptions = useMemo(() => {
    return providers
      .filter((p) => {
        if (!p.user_id) return false
        if (p.clinic_id === clinicId) return true
        return (
          p.assigned_clinics?.some((c) => c.clinic_id === clinicId) ?? false
        )
      })
      .map((p) => ({
        user_id: p.user_id,
        label: `${p.first_name} ${p.last_name}`.trim(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [clinicId, providers])

  const extensionOptions = useMemo(() => {
    const fromExt = internalExtensions
      .filter((e) => e.clinic_id === clinicId)
      .map((e) => String(e.extension_number))
    const fromDevices = phoneDevices
      .filter((d) => d.clinic_id === clinicId)
      .map((d) => String(d.device_extension_number || ''))
    const all = Array.from(new Set([...fromExt, ...fromDevices])).filter(
      (v) => v && isExtensionInRange(v),
    )
    return all.sort((a, b) => Number(a) - Number(b))
  }, [clinicId, internalExtensions, phoneDevices])

  const hasUnsavedChanges = useCallback((): boolean => {
    return !areValuesEqual(
      [
        deviceData.device_name,
        deviceData.device_type,
        deviceData.team as string,
        deviceData.device_extension_number as string,
        deviceData.assigned_user_id as string,
        deviceData.pstn_number as string,
        deviceData.record_inbound_calls as boolean,
        deviceData.record_outbound_calls as boolean,
      ],
      [
        baseline.device_name,
        baseline.device_type,
        baseline.team as string,
        baseline.device_extension_number as string,
        baseline.assigned_user_id as string,
        baseline.pstn_number as string,
        baseline.record_inbound_calls as boolean,
        baseline.record_outbound_calls as boolean,
      ],
    )
  }, [baseline, deviceData])

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {}
    if (!clinicId) errors.clinic = 'No clinic selected.'
    if (!deviceData.device_name?.trim()) {
      errors.device_name = 'Device name is required.'
    }
    if (!deviceData.team?.trim()) {
      errors.team = 'Team is required.'
    } else if (!(teams as readonly string[]).includes(deviceData.team.trim())) {
      errors.team = 'Invalid team.'
    }
    const ext = String(deviceData.device_extension_number || '').replace(
      /\D/g,
      '',
    )
    if (!ext) {
      errors.device_extension_number = 'Extension number is required.'
    } else if (!isExtensionInRange(ext)) {
      errors.device_extension_number = 'Extension must be between 100 and 900.'
    }
    if (
      deviceData.device_type === 'soft_phone' &&
      !deviceData.assigned_user_id
    ) {
      errors.assigned_user_id = 'Assign a user for soft phones.'
    }
    if (
      (deviceData.device_type === 'desk_phone' ||
        deviceData.device_type === 'mobile_phone') &&
      !isE164(deviceData.pstn_number)
    ) {
      errors.pstn_number =
        'PSTN number must be in E.164 format (e.g. +15555550100).'
    }

    const nameDup = phoneDevices.some(
      (d) =>
        d.clinic_phone_device_id !== deviceData.clinic_phone_device_id &&
        (d.device_name || '').trim().toLowerCase() ===
          deviceData.device_name.trim().toLowerCase(),
    )
    if (nameDup) errors.device_name = 'This device name is already in use.'

    if (ext) {
      const extDup = phoneDevices.some(
        (d) =>
          d.clinic_phone_device_id !== deviceData.clinic_phone_device_id &&
          String(d.device_extension_number || '').replace(/\D/g, '') === ext,
      )
      if (extDup) {
        errors.device_extension_number =
          'This extension is already assigned to a device.'
      }
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [clinicId, deviceData, phoneDevices, teams])

  const handleChange = useCallback(
    (key: keyof IClinicPhoneDevice, value: string | boolean): void => {
      if (Object.keys(validationErrors).length > 0) setValidationErrors({})
      if (key === 'device_type' && initialDevice) return // locked on edit
      if (
        key === 'assigned_user_id' &&
        typeof value === 'string' &&
        value.trim() === ''
      ) {
        setDeviceData((prev) => ({ ...prev, assigned_user_id: null }))
        return
      }
      setDeviceData((prev) => ({ ...prev, [key]: value }))
    },
    [initialDevice, validationErrors],
  )

  const handleCallRecordingChange = useCallback(
    (next: CallRecordingMode): void => {
      const { record_inbound_calls, record_outbound_calls } =
        callRecordingModeToBooleans(next)
      setDeviceData((prev) => ({
        ...prev,
        record_inbound_calls,
        record_outbound_calls,
      }))
    },
    [],
  )

  const handleSave = useCallback(async (): Promise<void> => {
    if (!validateForm()) return
    const ext = String(deviceData.device_extension_number || '').replace(
      /\D/g,
      '',
    )

    if (initialDevice && deviceData.clinic_phone_device_id) {
      const updateData: Partial<IClinicPhoneDevice> = {
        device_name: deviceData.device_name,
        team: deviceData.team,
        device_extension_number: ext,
        assigned_user_id: deviceData.assigned_user_id ?? null,
        record_inbound_calls: !!deviceData.record_inbound_calls,
        record_outbound_calls: !!deviceData.record_outbound_calls,
      }
      if (deviceData.device_type !== 'voip_phone') {
        updateData.pstn_number = deviceData.pstn_number || null
      }
      setSaving(true)
      showLoading({ container: LOADING_CONTAINER })
      try {
        const res = await dispatch(
          updatePhoneDeviceAction({
            id: deviceData.clinic_phone_device_id,
            data: updateData,
          }),
        )
        if (updatePhoneDeviceAction.fulfilled.match(res)) {
          showToast(createSuccessToast('Saved', 'Device updated.'))
          onSaved()
        } else {
          showToast(
            createFailedToast(
              'Device',
              String(res.payload || 'Update failed.'),
            ),
          )
        }
      } catch (e) {
        showToast(createFailedToast('Device', 'Update failed.'))
      } finally {
        setSaving(false)
        hideLoading(LOADING_CONTAINER)
      }
      return
    }

    const payload: IClinicPhoneDevice = {
      ...deviceData,
      clinic_id: clinicId,
      device_extension_number: ext,
      sip_uri: null,
      sip_domain_override: null,
      pstn_number:
        deviceData.device_type === 'voip_phone'
          ? null
          : deviceData.pstn_number || null,
    }
    setSaving(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const res = await dispatch(createPhoneDeviceAction(payload))
      if (!createPhoneDeviceAction.fulfilled.match(res)) {
        showToast(
          createFailedToast('Device', String(res.payload || 'Create failed.')),
        )
        return
      }
      const { phone_device: created, sip_credentials_once: sipOnce } =
        res.payload

      if (sipOnce) {
        setSipOnceModal(sipOnce)
      }
      setDeviceData((prev) => ({ ...prev, ...created }))
      showToast(createSuccessToast('Saved', 'Device created.'))
      if (clinicId) void dispatch(fetchPhoneManagementData(clinicId))
      if (!sipOnce) onSaved()
    } catch (e) {
      showToast(createFailedToast('Device', 'Create failed.'))
    } finally {
      setSaving(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    clinicId,
    deviceData,
    dispatch,
    hideLoading,
    initialDevice,
    onSaved,
    showLoading,
    showToast,
    validateForm,
  ])

  const dismissSipOnceModal = useCallback(() => {
    setSipOnceModal(null)
    onSaved()
  }, [onSaved])

  const regenerateSip = useCallback(async (): Promise<void> => {
    const id = deviceData.clinic_phone_device_id
    if (
      !id ||
      !clinicId ||
      deviceData.device_type !== 'voip_phone' ||
      !deviceData.twilio_sip_credential_sid
    ) {
      showToast(
        createFailedToast(
          'SIP',
          'Save the device first to provision SIP credentials.',
        ),
      )
      return
    }
    setSipBusy(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const res = await dispatch(
        regenerateDeviceSipCredentialsAction({ id, clinic_id: clinicId }),
      )
      if (regenerateDeviceSipCredentialsAction.fulfilled.match(res)) {
        setSipOnceModal(res.payload)
        showToast(createSuccessToast('SIP', 'New SIP password generated.'))
      } else {
        showToast(
          createFailedToast(
            'SIP',
            String(res.payload || 'Failed to regenerate credentials.'),
          ),
        )
      }
    } catch (e) {
      showToast(createFailedToast('SIP', 'Failed to regenerate credentials.'))
    } finally {
      setSipBusy(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    clinicId,
    deviceData.clinic_phone_device_id,
    deviceData.device_type,
    deviceData.twilio_sip_credential_sid,
    dispatch,
    hideLoading,
    showLoading,
    showToast,
  ])

  const onClickCancel = useCallback(async (): Promise<void> => {
    if (hasUnsavedChanges()) setShowUnsavedChangesModal(true)
    else onCancel()
  }, [hasUnsavedChanges, onCancel])

  const discardUnsavedChanges = useCallback(async (): Promise<void> => {
    setShowUnsavedChangesModal(false)
    onCancel()
  }, [onCancel])

  const onHide = useCallback(async (): Promise<void> => {
    setShowUnsavedChangesModal(false)
  }, [])

  const submitE911Flow = useCallback(async (): Promise<void> => {
    if (
      !e911Form.customer_name.trim() ||
      !e911Form.address.trim() ||
      !e911Form.city.trim() ||
      !e911Form.region.trim() ||
      !e911Form.postal_code.trim()
    ) {
      showToast(createFailedToast('E911', 'Complete all address fields.'))
      return
    }
    const id = deviceData.clinic_phone_device_id
    if (!id || !clinicId) {
      showToast(
        createFailedToast('E911', 'Save the device first, then add E911.'),
      )
      return
    }
    showLoading({ container: LOADING_CONTAINER })
    try {
      const res = await dispatch(
        createDeviceE911AddressAction({
          clinic_id: clinicId,
          clinic_phone_device_id: id,
          customer_name: e911Form.customer_name.trim(),
          address: e911Form.address.trim(),
          city: e911Form.city.trim(),
          state: e911Form.region.trim(),
          zip: e911Form.postal_code.trim(),
          iso_country: 'US',
        }),
      )
      if (!createDeviceE911AddressAction.fulfilled.match(res)) {
        showToast(
          createFailedToast('E911', String(res.payload || 'Failed to save.')),
        )
        return
      }
      const e911 = res.payload.e911_address
      setDeviceData((prev) => ({
        ...prev,
        clinic_e911_address_id: e911.clinic_e911_address_id ?? null,
      }))
      setShowE911Modal(false)
      setE911Form(defaultE911Form)
      if (res.payload.validation_status === 'failed') {
        showToast(
          createFailedToast(
            'E911',
            res.payload.error_message ||
              'Twilio could not validate this address. You can edit and retry.',
          ),
        )
      } else {
        showToast(createSuccessToast('E911', 'Emergency address saved.'))
      }
      if (clinicId) void dispatch(fetchPhoneManagementData(clinicId))
    } catch (e) {
      showToast(createFailedToast('E911', 'Failed to save emergency address.'))
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    clinicId,
    deviceData.clinic_phone_device_id,
    defaultE911Form,
    dispatch,
    e911Form,
    hideLoading,
    showLoading,
    showToast,
  ])

  const removeE911FromDevice = useCallback(async (): Promise<void> => {
    const devId = deviceData.clinic_phone_device_id
    const e911Id = deviceData.clinic_e911_address_id
    if (!devId || !e911Id) return
    showLoading({ container: LOADING_CONTAINER })
    try {
      const unlinkRes = await dispatch(
        updatePhoneDeviceAction({
          id: devId,
          data: { clinic_e911_address_id: null },
        }),
      )
      if (!updatePhoneDeviceAction.fulfilled.match(unlinkRes)) {
        showToast(
          createFailedToast(
            'E911',
            String(unlinkRes.payload || 'Failed to unlink address.'),
          ),
        )
        return
      }
      const deleteRes = await dispatch(deleteE911AddressAction(e911Id))
      if (!deleteE911AddressAction.fulfilled.match(deleteRes)) {
        showToast(
          createFailedToast(
            'E911',
            String(deleteRes.payload || 'Failed to remove address.'),
          ),
        )
        return
      }
      setDeviceData((prev) => ({ ...prev, clinic_e911_address_id: null }))
      setPendingRemoveE911(false)
      showToast(createSuccessToast('E911', 'Emergency address removed.'))
      if (clinicId) void dispatch(fetchPhoneManagementData(clinicId))
    } catch (e) {
      showToast(
        createFailedToast('E911', 'Failed to remove emergency address.'),
      )
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    clinicId,
    deviceData.clinic_e911_address_id,
    deviceData.clinic_phone_device_id,
    dispatch,
    hideLoading,
    showLoading,
    showToast,
  ])

  return {
    baseline,
    callRecordingMode,
    deviceData,
    discardUnsavedChanges,
    extensionOptions,
    handleCallRecordingChange,
    handleChange,
    handleSave,
    hasUnsavedChanges,
    onClickCancel,
    onHide,
    saving,
    setHeaderButtons,
    setHeaderTitle,
    showUnsavedChangesModal,
    validationErrors,
    userOptions,

    // E911
    defaultE911Form,
    e911Form,
    e911Linked: !!deviceData.clinic_e911_address_id,
    pendingRemoveE911,
    removeE911FromDevice,
    savedE911,
    setE911Form,
    setPendingRemoveE911,
    setShowE911Modal,
    showE911Modal,
    submitE911Flow,

    // SIP
    dismissSipOnceModal,
    regenerateSip,
    sipBusy,
    sipOnceModal,
  }
}

export default useAddDevice
